import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-light ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default:
                    "bg-primary text-primary-foreground hover:bg-primary/90",
                primary:
                    "bg-linear-to-b from-slate-700 to-slate-800 text-white hover:bg-linear-to-b hover:from-slate-600 hover:to-slate-800",
                primaryLight:
                    "bg-blue-100 hover:bg-blue-200 text-blue-500 border border-blue-100",
                orOperatorLight:
                    "bg-violet-100 hover:bg-violet-200 text-violet-500 border border-violet-100",
                destructive:
                    "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                outline:
                    "border border-input bg-card hover:bg-accent hover:text-accent-foreground",
                outlineDestructive:
                    "border border-destructive text-destructive bg-transparent hover:bg-destructive hover:text-white",
                secondary:
                    "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                successLight: "bg-green-100 text-green-600 hover:bg-green-200",
                warningLight: "bg-amber-100 text-amber-600 hover:bg-amber-200",
                // Variant = SuccessLight
                link: "text-link hover:text-link-hover",
            },
            size: {
                default: "h-10 px-4 py-2",
                xs: "h-7 rounded-md px-4 text-xs",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                icon: "h-10 w-10",
                "icon-sm": "h-8 w-8",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

export interface ButtonProps
    extends
        React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        );
    },
);
Button.displayName = "Button";

export { Button, buttonVariants };
